/**
 * Format schedule start/end for the fixed left time column.
 * Clock times → `9:00a` / `9:00–10:30a`; fuzzy labels kept; missing → TBD.
 */

const FUZZY = new Set(["morning", "afternoon", "evening", "early", "night", "all day", "all-day"]);

function parseClock(raw: string): { h: number; m: number } | null {
  const t = raw.trim();
  const m24 = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(t);
  if (m24) return { h: Number(m24[1]), m: Number(m24[2]) };
  const m12 = /^(\d{1,2}):([0-5]\d)\s*([ap])\.?m?\.?$/i.exec(t);
  if (m12) {
    let h = Number(m12[1]);
    const m = Number(m12[2]);
    const ap = m12[3].toLowerCase();
    if (h === 12) h = 0;
    if (ap === "p") h += 12;
    return { h, m };
  }
  return null;
}

function formatClock(h: number, m: number, withPeriod: boolean): string {
  const ap = h >= 12 ? "p" : "a";
  let hour = h % 12;
  if (hour === 0) hour = 12;
  const mm = String(m).padStart(2, "0");
  return withPeriod ? `${hour}:${mm}${ap}` : `${hour}:${mm}`;
}

function formatSingle(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "TBD";
  const lower = trimmed.toLowerCase();
  if (FUZZY.has(lower)) {
    return lower === "all-day" || lower === "all day"
      ? "All day"
      : lower.charAt(0).toUpperCase() + lower.slice(1);
  }
  const clock = parseClock(trimmed);
  if (clock) return formatClock(clock.h, clock.m, true);
  // Already-human strings (e.g. "TBD", "noon") — show as-is, capped
  return trimmed.length > 12 ? `${trimmed.slice(0, 11)}…` : trimmed;
}

/**
 * Display label for itinerary time column. Never returns blank.
 */
export function formatEventHours(
  time?: string | null,
  endTime?: string | null,
): { label: string; isTbd: boolean } {
  const startRaw = time?.trim() ?? "";
  const endRaw = endTime?.trim() ?? "";
  if (!startRaw && !endRaw) return { label: "TBD", isTbd: true };

  if (!startRaw) return { label: formatSingle(endRaw), isTbd: false };

  const startClock = parseClock(startRaw);
  const endClock = endRaw ? parseClock(endRaw) : null;

  if (startClock && endClock) {
    const samePeriod =
      (startClock.h >= 12) === (endClock.h >= 12);
    if (samePeriod) {
      return {
        label: `${formatClock(startClock.h, startClock.m, false)}–${formatClock(endClock.h, endClock.m, true)}`,
        isTbd: false,
      };
    }
    return {
      label: `${formatClock(startClock.h, startClock.m, true)}–${formatClock(endClock.h, endClock.m, true)}`,
      isTbd: false,
    };
  }

  if (startClock && endRaw) {
    return {
      label: `${formatClock(startClock.h, startClock.m, true)}–${formatSingle(endRaw)}`,
      isTbd: false,
    };
  }

  if (startRaw && endRaw) {
    return {
      label: `${formatSingle(startRaw)}–${formatSingle(endRaw)}`,
      isTbd: false,
    };
  }

  return { label: formatSingle(startRaw), isTbd: false };
}
